/* eslint-disable no-unneeded-ternary */
import PropTypes from 'prop-types';
// form
import { Controller, useFieldArray, useFormContext } from 'react-hook-form';
// @mui
import {
  Box,
  Checkbox,
  Chip,
  Divider,
  FormControl,
  FormHelperText,
  InputLabel,
  MenuItem,
  OutlinedInput,
  Select,
  TextField,
} from '@mui/material';
import RequiredLabel from './RequiredLabel';

// ----------------------------------------------------------------------

RHFSelect.propTypes = {
  name: PropTypes.string,
  native: PropTypes.bool,
  children: PropTypes.node,
  helperText: PropTypes.node,
  maxHeight: PropTypes.number,
  required: PropTypes.bool,
  label: PropTypes.string,
};

export function RHFSelect({
  name,
  native,
  children,
  helperText,
  maxHeight = 220,
  required,
  label,
  ...other
}) {
  const { control } = useFormContext();

  const isChildrenExist = children?.length || 0;

  return (
    <Controller
      name={name}
      control={control}
      render={({ field, fieldState: { error } }) => (
        <TextField
          {...field}
          select
          label={<RequiredLabel required={required} label={label} />}
          fullWidth
          SelectProps={{
            native,
            MenuProps: {
              PaperProps: {
                sx: {
                  ...(!native && {
                    px: 1,
                    maxHeight: typeof maxHeight === 'number' ? maxHeight : 'unset',
                    '& .MuiMenuItem-root': {
                      px: 1,
                      borderRadius: 0.75,
                      typography: 'body2',
                      textTransform: 'capitalize',
                    },
                  }),
                },
              },
            },
            sx: { textTransform: 'capitalize' },
          }}
          error={!!error}
          helperText={error ? error?.message : helperText}
          {...other}
        >
          {isChildrenExist ? (
            children
          ) : (
            <MenuItem value="No Options" disabled>
              No Options
            </MenuItem>
          )}
        </TextField>
      )}
    />
  );
}

// ----------------------------------------------------------------------

RHFMultiSelect.propTypes = {
  name: PropTypes.string,
  chip: PropTypes.bool,
  label: PropTypes.string,
  options: PropTypes.array,
  checkbox: PropTypes.bool,
  placeholder: PropTypes.string,
  helperText: PropTypes.node,
  limitTags: PropTypes.number,
  onCallBack: PropTypes.func,
  sx: PropTypes.object,
  required: PropTypes.bool,
};

export function RHFMultiSelect({
  name,
  chip,
  label,
  options,
  checkbox,
  placeholder,
  helperText,
  limitTags,
  onCallBack,
  sx,
  required,
  ...other
}) {
  const { control,  setValue } = useFormContext();
 
  const { fields } = useFieldArray({
    control,
    name,
  });
  const fieldValue = fields || [];
  const areAllSelected = options.length > 0 && fieldValue.length === options.length;

  const handleOnChange = (event) => {
    const { value: selectedItem } = event.target;
    if (selectedItem?.includes('all')) {
      const allOptions = options?.map((item) => item.value);
      const newValue = areAllSelected ? [] : allOptions;

      setValue(name, newValue);

      if (onCallBack) onCallBack(newValue);
    } else {
      setValue(name, selectedItem);
      if (onCallBack) onCallBack(selectedItem);
    }
  };

  const renderValues = (selectedIds) => {
    const selectedItems = options?.filter((item) => selectedIds?.includes(item.value));
    if (!selectedItems.length && placeholder) {
      return (
        <Box component="em" sx={{ color: 'text.disabled' }}>
          {placeholder}
        </Box>
      );
    }
    if (chip) {
      const limitTagApplicable = limitTags && selectedItems.length > limitTags;
      const limitedTagsCount = selectedItems.length - Number(limitTags);
      return (
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
          {(limitTagApplicable ? selectedItems.slice(0, limitTags) : selectedItems).map((item) => (
            <Chip key={item.value} size="small" label={item.label} />
          ))}
          {limitTags && limitTagApplicable && (
            <Chip size="small" sx={{ fontWeight: 600 }} label={`+${limitedTagsCount} more`} />
          )}
        </Box>
      );
    }
    return selectedItems.map((item) => item.label).join(', ');
  };

  const isOptionsExist = options?.length > 0 || false;

  return (
    <Controller
      name={name}
      control={control}
      render={({ field, fieldState: { error } }) => (
        <FormControl sx={sx}>
          {label && (
            <InputLabel id={name}>
              {label}
              {required && <span style={{ color: 'red' }}>&nbsp; *</span>}
            </InputLabel>
          )}

          <Select
            {...field}
            multiple
            displayEmpty={!!placeholder}
            labelId={name}
            input={<OutlinedInput fullWidth label={label} error={!!error} />}
            onChange={handleOnChange}
            renderValue={renderValues}
            MenuProps={{
              PaperProps: {
                sx: { px: 1, maxHeight: 280 },
              },
            }}
            {...other}
          >
            {placeholder && (
              <MenuItem
                disabled
                value=""
                sx={{
                  py: 1,
                  px: 2,
                  borderRadius: 0.75,
                  typography: 'body2',
                }}
              >
                <em> {placeholder} </em>
              </MenuItem>
            )}
            {isOptionsExist && (
              <MenuItem
                value="all"
                sx={{
                  py: 1,
                  px: 2,
                  borderRadius: 0.75,
                  typography: 'body2',
                  ...(areAllSelected && {
                    fontWeight: 'fontWeightMedium',
                  }),
                  ...(checkbox && {
                    p: 0.25,
                  }),
                }}
              >
                {checkbox && <Checkbox disableRipple size="small" checked={areAllSelected} />}
                Select All
              </MenuItem>
            )}

            {checkbox && isOptionsExist && (
              <Divider
                sx={{
                  borderStyle: 'dashed',
                }}
              />
            )}

            {!isOptionsExist && (
              <MenuItem value="No Options" disabled>
                No Options
              </MenuItem>
            )}

            {isOptionsExist &&
              options.map((option) => {
                const selected = field?.value?.includes(option.value);

                return (
                  <MenuItem
                    key={option.value}
                    value={option.value}
                    sx={{
                      py: 1,
                      px: 2,
                      borderRadius: 0.75,
                      typography: 'body2',
                      ...(selected && {
                        fontWeight: 'fontWeightMedium',
                      }),
                      ...(checkbox && {
                        p: 0.25,
                      }),
                    }}
                  >
                    {checkbox && <Checkbox disableRipple size="small" checked={selected} />}

                    {option.label}
                  </MenuItem>
                );
              })}
          </Select>

          {(!!error || helperText) && (
            <FormHelperText error={!!error}>{error ? error?.message : helperText}</FormHelperText>
          )}
        </FormControl>
      )}
    />
  );
}

// ----------------------------------------------------------------------

RHFMultiSelect2.propTypes = {
  name: PropTypes.string,
  chip: PropTypes.bool,
  label: PropTypes.string,
  options: PropTypes.array,
  checkbox: PropTypes.bool,
  placeholder: PropTypes.string,
  helperText: PropTypes.node,
  limitTags: PropTypes.number,
  onCallBack: PropTypes.func,
  sx: PropTypes.object,
  required: PropTypes.bool,
  isChapter: PropTypes.bool,
};

export function RHFMultiSelect2({
  name,
  chip,
  label,
  options,
  checkbox,
  placeholder,
  helperText,
  limitTags,
  onCallBack,
  sx,
  required,
  isChapter,
  ...other
}) {
  const { control,  setValue } = useFormContext();
 
  const { fields } = useFieldArray({
    control,
    name,
  });
  const fieldValue = fields || [];
  const areAllSelected = options.length > 0 && fieldValue.length === options.length;

  const handleOnChange = (event) => {
    const { value: selectedItem } = event.target;
    if (selectedItem?.includes('all')) {
      const allOptions = options?.map((item) => item.value);
      const newValue = areAllSelected ? [] : allOptions;

      setValue(name, newValue);

      if (onCallBack) onCallBack(newValue);
    } else {
      setValue(name, selectedItem);
      if (onCallBack) onCallBack(selectedItem);
    }
  };

  const renderValues = (selectedIds) => {
    const selectedItems = options?.filter((item) => selectedIds?.includes(item.value));
    if (!selectedItems.length && placeholder) {
      return (
        <Box component="em" sx={{ color: 'text.disabled' }}>
          {placeholder}
        </Box>
      );
    }
    if (chip) {
      const limitTagApplicable = limitTags && selectedItems.length > limitTags;
      const limitedTagsCount = selectedItems.length - Number(limitTags);
      return (
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
          {(limitTagApplicable ? selectedItems.slice(0, limitTags) : selectedItems).map((item) => (
            <Chip key={item.value} size="small" label={item.label} />
          ))}
          {limitTags && limitTagApplicable && (
            <Chip size="small" sx={{ fontWeight: 600 }} label={`+${limitedTagsCount} more`} />
          )}
        </Box>
      );
    }
    return selectedItems.map((item) => item.label).join(', ');
  };

  const isOptionsExist = options?.length > 0 || false;

  return (
    <Controller
      name={name}
      control={control}
      render={({ field, fieldState: { error } }) => (
        <FormControl sx={sx}>
          {label && (
            <InputLabel id={name}>
              {label}
              {required && <span style={{ color: 'red' }}>&nbsp; *</span>}
            </InputLabel>
          )}

          <Select
            {...field}
            multiple
            displayEmpty={!!placeholder}
            labelId={name}
            input={<OutlinedInput fullWidth label={label} error={!!error} />}
            onChange={handleOnChange}
            renderValue={renderValues}
            MenuProps={{
              PaperProps: {
                sx: { px: 1, maxHeight: 280 },
              },
            }}
            {...other}
          >
            {placeholder && (
              <MenuItem
                disabled
                value=""
                sx={{
                  py: 1,
                  px: 2,
                  borderRadius: 0.75,
                  typography: 'body2',
                }}
              >
                <em> {placeholder} </em>
              </MenuItem>
            )}
            {isOptionsExist && (
              <MenuItem
                value="all"
                sx={{
                  py: 1,
                  px: 2,
                  borderRadius: 0.75,
                  typography: 'body2',
                  ...(areAllSelected && {
                    fontWeight: 'fontWeightMedium',
                  }),
                  ...(checkbox && {
                    p: 0.25,
                  }),
                }}
              >
                {checkbox && <Checkbox disableRipple size="small" checked={areAllSelected} />}
                Select All
              </MenuItem>
            )}

            {checkbox && isOptionsExist && (
              <Divider
                sx={{
                  borderStyle: 'dashed',
                }}
              />
            )}

            {!isOptionsExist && (
              <MenuItem value="No Options" disabled>
                No Options
              </MenuItem>
            )}
            {isOptionsExist &&
              options.map((option) => {
                const selected = field?.value?.includes(option.value);
                const isChapterId = option.value===process.env.REACT_APP_CHAPTER_ID;
                const isAllSelected = field?.value?.includes(process.env.REACT_APP_CHAPTER_ID);

                return (
                  <MenuItem
                    key={option.value}
                    value={option.value}
                   // disabled={isChapterId?false:isAllSelected}
                    sx={{
                      py: 1,
                      px: 2,
                      borderRadius: 0.75,
                      typography: 'body2',
                      ...(selected && {
                        fontWeight: 'fontWeightMedium',
                      }),
                      ...(checkbox && {
                        p: 0.25,
                      }),
                    }}
                  >
                    {checkbox && <Checkbox disableRipple size="small" checked={selected} 
                    // disabled={isChapterId?false:isAllSelected} 
                    />}

                    {option.label}
                  </MenuItem>
                );
              })}
          </Select>

          {(!!error || helperText) && (
            <FormHelperText error={!!error}>{error ? error?.message : helperText}</FormHelperText>
          )}
        </FormControl>
      )}
    />
  );
}
